import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'redux';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import withRouteHelpers from 'common/hoc/withRouteHelpers';
import useSeasons from 'studio/hooks/useSeasons';
import formatCurrency from 'studio/components/OfferPage/helpers/formatCurrency';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import isNonUnionWorkScheduleA from 'common/utilities/isNonUnionWorkScheduleA';
import SpecifiedCountryOnly from 'common/components/SpecifiedCountryOnly';
import isUnionWeeklyScheduleCode from 'common/utilities/isUnionWeeklySchedule';

const styles = theme => ({
  root: {
    display: 'grid',
    height: '100%',
    gridTemplateRows: '60px 60px 30px minmax(166px, max-content) 64px',
  },
  headerContainer: {
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    textAlign: 'center',
    color: theme.palette.secondary.contrastText,
  },
  subHeader: {
    padding: theme.spacing.unit,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  lineItemContainer: {
    display: 'grid',
    gridAutoRows: 'auto',
    paddingBottom: 12,
  },
  lineItem: {
    display: 'grid',
    gridTemplateColumns: '40% 60%',
    paddingLeft: '35px',
    paddingRight: '35px',
  },
  label: {
    fontSize: '1.1rem',
  },
  value: {
    fontSize: '1.1rem',
  },
  submitContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: 12,
  },
  subText: {
    textAlign: 'center',
  },
});

const getCrewMemberNames = (crewMembers = {}) => {
  const crewMemberIds = Object.keys(crewMembers).filter(id => crewMembers[id]);
  if (crewMemberIds.length >= 5) return `${crewMemberIds.length} crew members`;
  const crewMemberNames = crewMemberIds
    .map(id => {
      const { profile = {} } = crewMembers[id] || {};
      const { firstName = '', lastName = '' } = profile || {};
      return `${firstName} ${lastName}`;
    })
    .join(', ');
  return crewMemberNames;
};

// TODO: this component needs refactoring
const OfferConfirmationModal = props => {
  const {
    formData = {},
    onSubmit,
    submitInProgress = false,
    classes = {},
    onClose,
    crew = {},
    projectId,
  } = props;
  const {
    offerDate = {},
    termsOfEmployment = {},
    allowances = {},
    termsOfHire,
    dealNotes = [],
  } = formData || {};
  const { startDate } = offerDate || {};
  const {
    occupation: { label: occupation },
    union: { label: union, isNonUnion },
    workSchedule: { label: workSchedule, value: workScheduleCode },
    employmentClassification,
    jobDescription,
    season,
    hiringStatus,
  } = termsOfHire || {};
  const [dealNote = {}] = dealNotes || [];
  const { notes: dealNoteText = '' } = dealNote || {};
  const {
    idleLocationSeventhDayRatePerHour,
    idleLocationSixthDayRatePerHour,
    locationGuaranteedHours,
    locationRatePerHour,
    locationRatePerWeek,
    overtimeRatePerHour,
    studioGuaranteedHours,
    studioRatePerHour,
    studioRatePerWeek,
    guaranteedHoursFrequency,
    ratePerHour,
    isPrcanRateTable,
    dailyRate,
    weeklyRate,
    weeklyRateDistant,
    rate,
    rateDistant,
    guaranteedHours,
    guaranteedHoursDistant,
    guaranteedHours6thDay,
    guaranteedHours7thDay,
    guaranteedHours6thDayDistant,
    guaranteedHours7thDayDistant,
    payIdleDaysDistant,
    payGoldAt,
    payGoldAtDistant,
    payAtScale,
    payAtScaleDistant,
    idleAtScaleDistant,
    goldAtScale,
    goldAtScaleDistant,
    numberOfDaysPerWorkWeek,
    sixthDayPayMultiplication,
    seventhDayPayMultiplication,
    negotiatedContractId,
  } = termsOfEmployment || {};

  const {
    boxRentalAllowance: {
      amount: boxRentalAllowance = null,
      per: boxRentalAllowancePer,
    } = {},
    carAllowance: { amount: carAllowance = null, per: carAllowancePer } = {},
    computerRentalAllowance: {
      amount: computerRentalAllowance = null,
      per: computerRentalAllowancePer,
    } = {},
    housingAllowance: {
      amount: housingAllowance = null,
      per: housingAllowancePer,
    } = {},
    mobilePhoneAllowance: {
      amount: mobilePhoneAllowance = null,
      per: mobilePhoneAllowancePer,
    } = {},
    perDiemAllowance: {
      amount: perDiemAllowance = null,
      per: perDiemAllowancePer,
    } = {},
  } = allowances || {};

  const { data: seasons = [] } = useSeasons(projectId);
  const flags = useFeatureFlags();
  const isNonUnionScheduleAFlagActive = flags.includes('nonUnionSchedules');
  const isNonUnionScheduleCFlagActive = flags.includes('nonUnionScheduleC');
  const isNonUnionScheduleBFlagActive = flags.includes('nonUnionScheduleB');
  const isNonUnionScheduleDFlagActive = flags.includes('nonUnionScheduleD');
  const isUnionScheduleAFlagActive = flags.includes('UnionScheduleA');
  const isUnionWeeklyScheduleFlagActive = flags.includes('UnionWeeklySchedule');

  const { label: seasonLabel = '' } =
    seasons.find(({ id: seasonId }) => seasonId === season) || {};
  const hiringStatusDescription = {
    full_time: 'Full Time',
    variable: 'Variable',
  };
  const crewHiringStatus = isPrcanRateTable
    ? null
    : hiringStatusDescription[hiringStatus];

  const isUnionScheduleA =
    workScheduleCode === 'A' &&
    !isNonUnion &&
    isUnionScheduleAFlagActive &&
    !isPrcanRateTable;

  const isUnionWeeklySchedule =
    isUnionWeeklyScheduleCode(workScheduleCode) &&
    !isNonUnion &&
    isUnionWeeklyScheduleFlagActive &&
    !isPrcanRateTable;

  const nonUnionScheduleALabels =
    isNonUnionScheduleAFlagActive &&
    isNonUnion &&
    isNonUnionWorkScheduleA(workScheduleCode)
      ? [
          {
            label: 'Apply Hours per Day to 6th Day',
            value: guaranteedHours6thDay ? 'Yes' : 'No',
            countryCode: 'US',
          },
          {
            label: 'Apply Hours per Day to 7th Day',
            value: guaranteedHours7thDay ? 'Yes' : 'No',
            countryCode: 'US',
          },
        ]
      : [];

  const isNonUnionScheduleC =
    isNonUnionScheduleCFlagActive &&
    isNonUnion &&
    ['C', 'E', 'CN'].includes(workScheduleCode);
  const isNonUnionScheduleB =
    isNonUnionScheduleBFlagActive && isNonUnion && workScheduleCode === 'B';
  const isNonUnionScheduleD =
    isNonUnionScheduleDFlagActive && isNonUnion && workScheduleCode === 'D';

  const nonUnionScheduleCLabels = isNonUnionScheduleC
    ? [
        {
          label: 'Pay 6th Day at',
          value:
            sixthDayPayMultiplication > 0
              ? `${sixthDayPayMultiplication}x`
              : 'No additional pay',
          countryCode: 'US',
        },
        {
          label: 'Pay 7th Day at',
          value:
            seventhDayPayMultiplication > 0
              ? `${seventhDayPayMultiplication}x`
              : 'No additional pay',
          countryCode: 'US',
        },
      ]
    : [];

  const isPrCanWeekly =
    isPrcanRateTable && guaranteedHoursFrequency === 'weekly';
  const nonUnionHourlyRateLabels =
    isNonUnionScheduleB || isPrCanWeekly
      ? {
          label: 'Hours per Week',
          value: guaranteedHours,
        }
      : { label: 'Hours Per Day', value: guaranteedHours };

  let negotiatedContractTermsLabel = null;
  if (!!negotiatedContractId) {
    negotiatedContractTermsLabel = negotiatedContractId === 'N' ? 'No' : 'Yes';
  }

  // let lineItemsToDisplay = [];
  let termsOfEmploymentItemsDefault = [
    { label: 'Studio - Rate Per Hour', value: studioRatePerHour, dollar: true },
    { label: 'Studio - Rate Per Week', value: studioRatePerWeek, dollar: true },
    { label: 'Studio - Guaranteed Hours', value: studioGuaranteedHours },
    {
      label: 'Rate Per Hour',
      value: ratePerHour ? formatCurrency(ratePerHour) : null,
      dollar: true,
    },
    {
      label: 'Overtime Rate Per Hour',
      value: overtimeRatePerHour,
      dollar: true,
    },
    nonUnionHourlyRateLabels,
    {
      label: 'Rate Per Day',
      value: weeklyRate && isNonUnion ? null : rate,
      dollar: true,
    },
    {
      label: isPrcanRateTable ? 'Rate Per Day' : 'Daily Rate',
      value: isNonUnionScheduleD && !isPrcanRateTable ? rate : dailyRate,
      dollar: true,
    },
    {
      label: isPrcanRateTable ? 'Rate Per Week' : 'Weekly Rate',
      value: isNonUnionScheduleC && !isPrcanRateTable ? rate : weeklyRate,
      dollar: true,
    },
    ...nonUnionScheduleALabels,
    ...nonUnionScheduleCLabels,
    isNonUnionScheduleC || isNonUnionScheduleD
      ? {}
      : {
          label: 'Number Of Days Per Work Week',
          value: numberOfDaysPerWorkWeek,
        },
    {
      label: 'Distant - Rate Per Hour',
      value: locationRatePerHour,
      dollar: true,
    },
    {
      label: 'Distant - Rate Per Week',
      value: locationRatePerWeek,
      dollar: true,
    },
    { label: 'Distant - Guaranteed Hours', value: locationGuaranteedHours },
    {
      label: 'Distant - Idle Pay on Sixth Day',
      value: idleLocationSixthDayRatePerHour,
      dollar: true,
      countryCode: 'US',
    },
    {
      label: 'Distant - Idle Pay on Seventh Day',
      value: idleLocationSeventhDayRatePerHour,
      dollar: true,
      countryCode: 'US',
    },
  ];

  let termsOfEmploymentScheduleA = [
    {
      label: 'Studio - Rate Per Hour',
      value: rate,
      dollar: true,
      scaleValue: payAtScale ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Rate Per Hour',
      value: rateDistant,
      dollar: true,
      scaleValue: payAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Studio - Hours Per Day',
      value: guaranteedHours,
    },
    {
      label: 'Distant - Hours Per Day',
      value: guaranteedHoursDistant,
    },
    {
      label: 'Studio - Apply Hours to 6th Day',
      value: guaranteedHours6thDay ? 'Yes' : 'No',
    },
    {
      label: 'Studio - Apply Hours to 7th Day',
      value: guaranteedHours7thDay ? 'Yes' : 'No',
    },
    {
      label: 'Distant - Apply Hours to 6th Day',
      value: guaranteedHours6thDayDistant ? 'Yes' : 'No',
    },
    {
      label: 'Distant - Apply Hours to 7th Day',
      value: guaranteedHours7thDayDistant ? 'Yes' : 'No',
    },
    {
      label: 'Distant - Pay Idle Days at',
      value: payIdleDaysDistant,
      dollar: true,
      scaleValue: idleAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Studio - Pay Gold at',
      value: payGoldAt,
      dollar: true,
      scaleValue: goldAtScale ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Pay Gold at',
      value: payGoldAtDistant,
      dollar: true,
      scaleValue: goldAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
  ];

  let termsOfEmploymentWeeklySchedule = [
    {
      label: 'Studio - Weekly Rate',
      value: weeklyRate,
      dollar: true,
      scaleValue: payAtScale ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Weekly Rate',
      value: weeklyRateDistant,
      dollar: true,
      scaleValue: payAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Studio - Rate Per Hour',
      value: rate,
      dollar: true,
      scaleValue: payAtScale ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Rate Per Hour',
      value: rateDistant,
      dollar: true,
      scaleValue: payAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Studio - Hours Per Week',
      value: guaranteedHours,
      dollar: true,
      scaleValue: payAtScale ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Hours Per Week',
      value: guaranteedHoursDistant,
      dollar: true,
      scaleValue: payAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Pay Idle Days at',
      value: payIdleDaysDistant,
      dollar: true,
      scaleValue: idleAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Studio - Pay Gold at',
      value: payGoldAt,
      dollar: true,
      scaleValue: goldAtScale ? '(Scale)' : '(Negotiated)',
    },
    {
      label: 'Distant - Pay Gold at',
      value: payGoldAtDistant,
      dollar: true,
      scaleValue: goldAtScaleDistant ? '(Scale)' : '(Negotiated)',
    },
  ];

  let termsOfEmploymentItems;
  if (isUnionScheduleA) {
    termsOfEmploymentItems = termsOfEmploymentScheduleA;
  } else if (isUnionWeeklySchedule) {
    termsOfEmploymentItems = termsOfEmploymentWeeklySchedule;
  } else {
    termsOfEmploymentItems = termsOfEmploymentItemsDefault;
  }

  const lineItems = [
    { label: 'Start Date', value: startDate },
    { label: 'Season', value: seasonLabel },
    { label: 'Union', value: union },
    { label: 'Occupation', value: occupation },
    { label: 'Work Schedule', value: workSchedule },
    { label: 'Job Description', value: jobDescription },
    {
      label: 'Employment Classification',
      value: (employmentClassification || '').toUpperCase(),
      countryCode: 'US',
    },
    {
      label: 'Employment Classification',
      value: (employmentClassification === 'w2'
        ? 'individual'
        : employmentClassification || ''
      ).toUpperCase(),
      countryCode: 'CA',
    },
    { label: 'Hiring Status', value: crewHiringStatus },
    { label: 'Deal Notes', value: dealNoteText },
    {
      label: 'Use Negotiated Contract Terms',
      value: negotiatedContractTermsLabel,
    },
    ...termsOfEmploymentItems,
    {
      label: 'Box Rental Allowance',
      value: boxRentalAllowance,
      per: boxRentalAllowancePer,
      dollar: true,
    },
    {
      label: 'Car Allowance',
      value: carAllowance,
      per: carAllowancePer,
      dollar: true,
    },
    {
      label: 'Computer Rental Allowance',
      value: computerRentalAllowance,
      per: computerRentalAllowancePer,
      dollar: true,
    },
    {
      label: 'Housing Allowance',
      value: housingAllowance,
      per: housingAllowancePer,
      dollar: true,
    },
    {
      label: 'Mobile Phone Allowance',
      value: mobilePhoneAllowance,
      per: mobilePhoneAllowancePer,
      dollar: true,
    },
    {
      label: 'Per Diem Allowance',
      value: perDiemAllowance,
      per: perDiemAllowancePer,
      dollar: true,
    },

    { label: 'Crew Members', value: getCrewMemberNames(crew) },
  ];

  const lineItemsToDisplay = lineItems.filter(({ value }) => !!value);

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <Typography variant="h4" className={classes.header}>
          Confirm Offer
        </Typography>
      </div>
      <Typography variant="h5" className={classes.subHeader}>
        Offer Terms
      </Typography>
      <Typography variant="subtitle1" className={classes.subText}>
        Are you sure you want to create this offer?
      </Typography>
      <div className={classes.lineItemContainer}>
        {lineItemsToDisplay
          .filter(({ value }) => !!value)
          .map(
            ({ label, value, per, dollar, countryCode, scaleValue = null }) => (
              <SpecifiedCountryOnly
                countryCode={countryCode}
                key={`${label}-${value}`}
              >
                <div className={classes.lineItem}>
                  <Typography
                    variant="body1"
                    className={classes.label}
                    data-test-id={`OfferConfirmationModal-label-${label}`}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.value}
                    data-test-id={`OfferConfirmationModal-value-${label}${value}`}
                  >
                    {dollar ? '$' : ''}
                    {dollar ? formatCurrency(value) : value}
                    {scaleValue ? ` ${scaleValue}` : ''}
                    {per && ` / ${per}`}
                  </Typography>
                </div>
              </SpecifiedCountryOnly>
            ),
          )}
      </div>
      <div className={classes.submitContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          disabled={submitInProgress}
          data-test-id="OfferConfirmationModal-cancelButton"
        >
          No, Take me back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={onSubmit}
          disabled={submitInProgress}
          data-test-id="OfferConfirmationModal-button"
        >
          {submitInProgress ? 'Creating Offer...' : 'Yes, Create Offer'}
        </Button>
      </div>
    </div>
  );
};

export default compose(
  withRouteHelpers,
  withStyles(styles),
)(OfferConfirmationModal);
